import { ApiService } from "../services/api-service.js";
import * as RX from "rxjs";
import { array, option, record } from "fp-ts";
import { some, none, Option } from "fp-ts/lib/Option.js";
import { Failure } from "../helpers/failure.js";
import { pipe } from "fp-ts/lib/function.js";
import { Dictionary, toDictionaryByKey } from "../models/dictionary.js";
import { Map, MapSet } from "../models/map.js";
import { UGExhibition } from "../models/exhibition.js";
import { memoize } from "lodash-es";
import {
  ApiElement,
  ApiLocationData,
  ApiModule,
} from "../models/api/module.js";
import { ApiExhibition } from "../models/api/exhibition.js";
import { UGElement, UGLocationData, UGModule } from "../models/module.js";
import { ApiTemplate } from "../models/api/template.js";

import Handlebars from "handlebars";

export class App {
  private static apiService: Option<ApiService>;

  public static setApiService(apiService: ApiService) {
    this.apiService = option.some(apiService);
  }

  public static getExhibitionFromId = memoize(this._getExhibitionById);
  public static getExhibitionFromSlug = memoize(this._getExhibitionBySlug);

  private static _getExhibitionById(
    exhibitionId: number
  ): RX.Observable<UGExhibition> {
    return this._getExhibition(exhibitionId, (apiService, id) =>
      apiService.getExhibitionFromId(id)
    );
  }

  private static _getExhibitionBySlug(
    exhibitionSlug: string
  ): RX.Observable<UGExhibition> {
    return this._getExhibition(exhibitionSlug, (apiService, slug) =>
      apiService.getExhibitionFromSlug(slug)
    );
  }

  private static _getExhibition<T>(
    identifier: T,
    getExhibition: (apiService: ApiService, identifier: T) => RX.Observable<any>
  ): RX.Observable<UGExhibition> {
    const apiService = pipe(
      this.apiService,
      option.getOrElseW(() =>
        Failure("You must set an APIService before requesting an exhibition")
      )
    );

    return getExhibition(apiService, identifier).pipe(
      RX.switchMap((exhibition) => {
        const maps$ = apiService.getExhibitionMaps(exhibition.id);
        const modules$ = this.getModules(apiService, exhibition.id);

        return RX.combineLatest({
          exhibition: RX.of(exhibition),
          modules: modules$,
          maps: maps$,
        }).pipe(
          RX.map(({ exhibition, modules, maps }) => ({
            id: exhibition.id,
            name: exhibition.name,
            slug: exhibition.slug,
            modules: modules,
            mapSet: this.createMapset(exhibition, maps),
          }))
        );
      })
    );
  }

  private static createMapset(exhibition: ApiExhibition, maps: Map[]): MapSet {
    return {
      maps: maps,
      initialMap: pipe(
        maps,
        array.findFirst((map) => map.id == exhibition.defaultMapId),
        option.getOrElseW(() =>
          Failure("Exhibition map set does not include exhibition default map")
        )
      ),
    };
  }

  private static getModules(
    apiService: ApiService,
    exhibitionId: number
  ): RX.Observable<Dictionary<UGModule>> {
    return apiService.getModules(exhibitionId).pipe(
      RX.switchMap((modules: ApiModule[]) => {
        return this.enrichApiModules(apiService, exhibitionId, modules);
      })
    );
  }

  private static enrichApiModules(
    apiService: ApiService,
    exhibitionId: number,
    modules: ApiModule[]
  ): RX.Observable<Dictionary<UGModule>> {
    return RX.combineLatest(
      modules.map((module) => {
        // for each module, get each api result for that module
        // its templates and its list of elements
        return RX.combineLatest({
          module: RX.of(module),
          elements: apiService.getModuleElements(exhibitionId, module.id),
          thumbnailTemplate: apiService.getElementThumbnailTemplate(
            exhibitionId,
            module.id
          ),
          summaryTemplate: apiService.getElementSummaryTemplate(
            exhibitionId,
            module.id
          ),
          detailTemplate: apiService.getElementDetailTemplate(
            exhibitionId,
            module.id
          ),
        });
      })
    ).pipe(
      // Take all that data and construct a more convinient object with it. The UGModule object type
      RX.map((modulesWithApiResults) => {
        // turn list of modules into dictionary
        const dictionaryOfApiResults = pipe(
          modulesWithApiResults,
          toDictionaryByKey(
            (moduleAndApiResults) => moduleAndApiResults.module.slug
          )
        );
        // turn list of modules into dictionary
        const elementsByModuleSlug = this.createElementRelation(
          pipe(
            dictionaryOfApiResults,
            record.map((d) => d.elements)
          )
        );
        // update dictionaryOfApiResults with enriched UGElements
        const dataWithEnrichedElements = pipe(
          dictionaryOfApiResults,
          record.mapWithIndex((key, moduleAndData) => {
            return { ...moduleAndData, elements: elementsByModuleSlug[key] };
          })
        );

        return pipe(
          dataWithEnrichedElements,
          record.map(this.getModuleResources)
        );
      })
    );
  }
  private static getModuleResources(data: {
    elements: UGElement[];
    module: ApiModule;
    thumbnailTemplate: ApiTemplate;
    summaryTemplate: ApiTemplate;
    detailTemplate: ApiTemplate;
  }): UGModule {
    return {
      ...data.module,
      elements: data.elements,
      templates: {
        detail: Handlebars.compile(data.detailTemplate.template),
        detailTimestamp: Handlebars.compile(
          data.detailTemplate.timestampTemplate
        ),
        summary: Handlebars.compile(data.summaryTemplate.template),
        summaryTimestamp: Handlebars.compile(
          data.summaryTemplate.timestampTemplate
        ),
        thumbnail: Handlebars.compile(data.thumbnailTemplate.template),
        thumbnailTimestamp: Handlebars.compile(
          data.thumbnailTemplate.timestampTemplate
        ),
      },
    };
  }

  private static convertAPILocationsToUGLocations(
    locationData: ApiLocationData
  ): UGLocationData {
    const optionalStringToUrl = (str: string | false | null) =>
      pipe(
        str,
        option.fromNullable,
        option.flatMap((x) => (x === false ? none : some(x))),
        option.map((x) => new URL(x))
      );

    return {
      ...locationData,
      pathCustomIcon: optionalStringToUrl(locationData.pathCustomIcon),
      pathCustomIcons: optionalStringToUrl(locationData.pathCustomIcons),
    };
  }

  private static createElementRelation(
    ElementsByModuleSlug: Dictionary<ApiElement[]>
  ): Dictionary<UGElement[]> {
    return pipe(
      ElementsByModuleSlug,
      record.map((elements) =>
        pipe(
          elements,
          array.map((element) => {
            return {
              ...element,
              locationData: pipe(
                element.locationData,
                this.convertAPILocationsToUGLocations
              ),
            } as UGElement;
          }),
          array.map((element) => {
            element.relations.forEach((relation) => {
              // @ts-ignore
              // If exhibitor has modules on different exhibitions, modules not on the active exhibition will not be in the json for that moduletype.
              // That is not an error! The exhibitors relations are not filtered by exhbition by design
              relation.element = ElementsByModuleSlug[relation.postType]?.find(
                (other) => other.id == relation.id
              );
            });
            return element;
          })
        )
      )
    );
  }
}
